import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Loader2 } from 'lucide-react';

// ============================================
// Types
// ============================================

interface PageData {
  id: string;
  title: string;
  slug: string;
  content: { blocks: Block[] };
  metaTitle?: string;
  metaDescription?: string;
}

interface Block {
  type: string;
  data: Record<string, unknown>;
}

interface ColumnData {
  blocks: { type: string; data: Record<string, unknown> }[];
}

// ============================================
// DynamicPage: fetches and renders a page by slug
// ============================================

export function DynamicPage({ slug }: { slug: string }) {
  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['public-page', slug],
    queryFn: async () => {
      const response = await api.get<PageData>(`/pages/public/${slug}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (isError || !page) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-4xl font-bold text-gray-900">Page Not Found</h1>
        <p className="mt-4 text-lg text-gray-600">
          The page you are looking for does not exist or has not been published.
        </p>
        <a
          href="#"
          className="mt-8 inline-block rounded-lg bg-primary-600 px-6 py-3 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
        >
          Go Home
        </a>
      </div>
    );
  }

  // Update document title
  if (page.metaTitle || page.title) {
    document.title = page.metaTitle || page.title;
  }

  const blocks = page.content?.blocks || [];

  return (
    <div>
      {blocks.map((block, index) => (
        <PageBlock key={index} block={block} />
      ))}
      {blocks.length === 0 && (
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="text-3xl font-bold text-gray-900">{page.title}</h1>
        </div>
      )}
    </div>
  );
}

// ============================================
// PageBlock: renders a single block for the public site
// ============================================

function PageBlock({ block }: { block: Block }) {
  const { type, data } = block;

  switch (type) {
    case 'hero':
      return <HeroBlock data={data} />;
    case 'heading':
      return <HeadingBlock data={data} />;
    case 'paragraph':
      return <ParagraphBlock data={data} />;
    case 'image':
      return <ImageBlock data={data} />;
    case 'cta':
      return <CtaBlock data={data} />;
    case 'video':
      return <VideoBlock data={data} />;
    case 'divider':
      return <DividerBlock data={data} />;
    case 'spacer':
      return <SpacerBlock data={data} />;
    case 'columns':
      return <ColumnsBlock data={data} />;
    default:
      return null;
  }
}

// ============================================
// Block components
// ============================================

function HeroBlock({ data }: { data: Record<string, unknown> }) {
  const title = (data.title as string) || '';
  const subtitle = (data.subtitle as string) || '';

  if (!title && !subtitle) return null;

  return (
    <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white">
      <div className="absolute inset-0 bg-black/10" />
      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-6 text-xl text-primary-100 max-w-3xl mx-auto">
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}

function HeadingBlock({ data }: { data: Record<string, unknown> }) {
  const level = (data.level as string) || 'h2';
  const text = (data.text as string) || '';

  if (!text) return null;

  const wrapper = "mx-auto max-w-4xl px-4 sm:px-6 lg:px-8";

  switch (level) {
    case 'h2':
      return (
        <div className={wrapper}>
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">{text}</h2>
        </div>
      );
    case 'h3':
      return (
        <div className={wrapper}>
          <h3 className="text-2xl font-semibold text-gray-900 mt-10 mb-3">{text}</h3>
        </div>
      );
    case 'h4':
      return (
        <div className={wrapper}>
          <h4 className="text-xl font-semibold text-gray-800 mt-8 mb-2">{text}</h4>
        </div>
      );
    default:
      return (
        <div className={wrapper}>
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">{text}</h2>
        </div>
      );
  }
}

function ParagraphBlock({ data }: { data: Record<string, unknown> }) {
  const text = (data.text as string) || '';

  if (!text) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-2">
      <p className="text-lg leading-relaxed text-gray-700 whitespace-pre-wrap">
        {text}
      </p>
    </div>
  );
}

function ImageBlock({ data }: { data: Record<string, unknown> }) {
  const url = (data.url as string) || '';
  const alt = (data.alt as string) || '';
  const caption = (data.caption as string) || '';

  if (!url) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6">
      <figure>
        <img
          src={url}
          alt={alt}
          className="w-full rounded-xl shadow-sm object-cover"
          loading="lazy"
        />
        {caption && (
          <figcaption className="mt-3 text-center text-sm text-gray-500">
            {caption}
          </figcaption>
        )}
      </figure>
    </div>
  );
}

function CtaBlock({ data }: { data: Record<string, unknown> }) {
  const text = (data.text as string) || '';
  const url = (data.url as string) || '#';
  const style = (data.style as string) || 'primary';

  if (!text) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 text-center">
      <a
        href={url}
        className={
          style === 'secondary'
            ? 'inline-block rounded-lg border-2 border-primary-600 px-8 py-3 text-base font-semibold text-primary-600 hover:bg-primary-50 transition-colors'
            : 'inline-block rounded-lg bg-primary-600 px-8 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors'
        }
      >
        {text}
      </a>
    </div>
  );
}

function VideoBlock({ data }: { data: Record<string, unknown> }) {
  const url = (data.url as string) || '';
  const caption = (data.caption as string) || '';
  const embedUrl = getVideoEmbedUrl(url);

  if (!embedUrl) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6">
      <div className="relative aspect-video rounded-xl overflow-hidden shadow-sm bg-gray-900">
        <iframe
          src={embedUrl}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Video"
        />
      </div>
      {caption && (
        <p className="mt-3 text-center text-sm text-gray-500">{caption}</p>
      )}
    </div>
  );
}

function DividerBlock({ data }: { data: Record<string, unknown> }) {
  const style = (data.style as string) || 'solid';

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6">
      <hr
        className="border-gray-200"
        style={{ borderStyle: style }}
      />
    </div>
  );
}

function SpacerBlock({ data }: { data: Record<string, unknown> }) {
  const height = (data.height as number) || 48;

  return <div style={{ height }} aria-hidden="true" />;
}

function ColumnsBlock({ data }: { data: Record<string, unknown> }) {
  const count = (data.count as number) || 2;
  const columns = (data.columns as ColumnData[]) || [];

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6">
      <div
        className={
          count === 3
            ? 'grid gap-8 md:grid-cols-3'
            : 'grid gap-8 md:grid-cols-2'
        }
      >
        {Array.from({ length: count }).map((_, i) => {
          const col = columns[i];
          if (!col || !col.blocks || col.blocks.length === 0) {
            return <div key={i} />;
          }

          return (
            <div key={i} className="space-y-4">
              {col.blocks.map((block, blockIndex) => (
                <ColumnInnerBlock key={blockIndex} block={block} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ColumnInnerBlock({ block }: { block: { type: string; data: Record<string, unknown> } }) {
  switch (block.type) {
    case 'paragraph':
      return (
        <p className="text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
          {(block.data.text as string) || ''}
        </p>
      );
    default:
      return null;
  }
}

// ============================================
// Video URL helper
// ============================================

function getVideoEmbedUrl(url: string): string | null {
  if (!url) return null;

  // YouTube
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytMatch) {
    return `https://www.youtube.com/embed/${ytMatch[1]}`;
  }

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return null;
}
